import { ClientLoggingService } from '../shared/client-logging.service';
import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { QueryDocumentSnapshot, DocumentData } from 'firebase/firestore';
import { FirestoreTaskTrackingService, TaskStatus } from './firestore-task-tracking.service';

/** Pagination cursor state for a project's task list. See Issue #1598. */
interface ProjectPaginationState {
  lastVisibleDoc: QueryDocumentSnapshot<DocumentData> | null;
  hasMore: boolean;
}

export interface ActiveTask {
  taskId: string;
  projectId: string;
  filename: string;
  taskType?: string; // Task type from Firestore (e.g., 'code-applicability', 'compliance-report', 'plan_ingestion')
  status: 'pending' | 'processing' | 'complete' | 'failed' | 'cancelled';
  progress: number;
  message: string;
  startTime: Date;
  endTime?: Date;
  progressSteps?: any[]; // Include progress steps for detailed step history
  metadata?: { [key: string]: any }; // Include metadata for additional context
  costAnalysis?: any; // Cost analysis data from MetaCostAnalysis proto
}

/**
 * Service for tracking active tasks within the current project scope.
 * Acts as a centralized subscription manager using Firestore as single source of truth.
 * Provides a project-scoped counter badge and task management functionality.
 *
 * Note: This service tracks tasks for the currently active project only, not across all
 * projects for the user. When navigating between projects, the service loads tasks
 * specific to that project via loadExistingTasksForProject().
 */
@Injectable({
  providedIn: 'root',
})
export class TaskCounterService {
  private firestoreTaskTrackingService = inject(FirestoreTaskTrackingService);

  private activeTasksSubject = new BehaviorSubject<ActiveTask[]>([]);
  private taskSubscriptions = new Map<string, any>();
  private lastCompletedCount = 0;
  /** Per-project pagination cursors for `loadMoreTasksForProject()`. See Issue #1598. */
  private projectPagination = new Map<string, ProjectPaginationState>();

  constructor() {
    ClientLoggingService.log('INFO', '[TaskCounterService] Initialized');

    // Reduced logging - only log significant changes
    this.activeTasksSubject.subscribe(tasks => {
      const activeTasks = (tasks || []).filter(t => t.status === 'pending' || t.status === 'processing');
      const completedTasks = (tasks || []).filter(t => t.status === 'complete');

      // Only log when there are active tasks or when tasks complete
      if (activeTasks.length > 0 || completedTasks.length !== this.lastCompletedCount) {
        ClientLoggingService.log(
          'INFO',
          '[TaskCounterService] Tasks updated. Active:',
          activeTasks.length,
          'Completed:',
          completedTasks.length
        );
        this.lastCompletedCount = completedTasks.length;
      }
    });
  }

  /**
   * Observable of all active tasks
   */
  get activeTasks$(): Observable<ActiveTask[]> {
    return this.activeTasksSubject.asObservable();
  }

  /**
   * Get current count of active (pending/processing) tasks
   */
  get activeTaskCount$(): Observable<number> {
    return new Observable<number>(observer => {
      this.activeTasks$.subscribe(tasks => {
        const activeCount = tasks.filter(task => task.status === 'pending' || task.status === 'processing').length;
        observer.next(activeCount);
      });
    });
  }

  /**
   * Add a task to be tracked
   */
  addTask(taskId: string, projectId: string, filename: string, metadata?: { [key: string]: any }): void {
    ClientLoggingService.log(
      'INFO',
      '[TaskCounterService] Adding task:',
      taskId,
      'for project:',
      projectId,
      'filename:',
      filename
    );

    const newTask: ActiveTask = {
      taskId,
      projectId,
      filename,
      status: 'pending',
      progress: 0,
      message: 'Task queued',
      startTime: new Date(),
      metadata,
    };

    // Add to current tasks
    const currentTasks = this.activeTasksSubject.value;
    const updatedTasks = [...currentTasks, newTask];
    this.activeTasksSubject.next(updatedTasks);

    ClientLoggingService.log('INFO', '[TaskCounterService] Updated tasks list. Total tasks:', updatedTasks.length);
    ClientLoggingService.log(
      'INFO',
      '[TaskCounterService] Active tasks:',
      (updatedTasks || []).filter(t => t.status === 'pending' || t.status === 'processing').length
    );

    // Subscribe to real-time updates for this task
    const subscription = this.firestoreTaskTrackingService.getTaskUpdates(taskId).subscribe({
      next: (taskStatus: TaskStatus) => {
        ClientLoggingService.log('INFO', '[TaskCounterService] Received task update for:', taskId, taskStatus);
        this.updateTaskStatus(taskId, taskStatus);
      },
      error: error => {
        ClientLoggingService.log('ERROR', '[TaskCounterService] Error tracking task:', taskId, error);
        this.updateTaskStatus(taskId, {
          taskId,
          status: 'failed',
          progress: 0,
          message: 'Failed to track task',
        });
      },
    });

    this.taskSubscriptions.set(taskId, subscription);
  }

  /**
   * Update task status from Firestore updates
   */
  private updateTaskStatus(taskId: string, taskStatus: TaskStatus): void {
    ClientLoggingService.log('INFO', '[TaskCounterService] 🔄 Updating task status:', {
      taskId,
      newStatus: taskStatus.status,
      progress: taskStatus.progress,
      message: taskStatus.message,
      progressSteps: taskStatus.progressSteps,
      progressStepsLength: taskStatus.progressSteps?.length || 0,
    });

    const currentTasks = this.activeTasksSubject.value;
    const taskIndex = currentTasks.findIndex(task => task.taskId === taskId);

    if (taskIndex !== -1) {
      const previousStatus = currentTasks[taskIndex].status;
      const updatedTask = {
        ...currentTasks[taskIndex],
        taskType: taskStatus.taskType,
        status: taskStatus.status,
        progress: taskStatus.progress,
        message: taskStatus.message,
        progressSteps: taskStatus.progressSteps,
        metadata: { ...currentTasks[taskIndex].metadata, ...taskStatus.metadata, ...taskStatus.result },
        costAnalysis: taskStatus.costAnalysis,
        startTime: taskStatus.createdAt ? new Date(taskStatus.createdAt) : currentTasks[taskIndex].startTime,
      };

      // Set end time if task is complete or failed using actual Firestore timestamps
      if (taskStatus.status === 'complete' || taskStatus.status === 'failed') {
        // Use actual completion timestamp from Firestore instead of current time
        const completionTimestamp = taskStatus.completedAt || taskStatus.failedAt || taskStatus.cancelledAt;
        if (completionTimestamp) {
          updatedTask.endTime = new Date(completionTimestamp);
        } else {
          // Fallback to current time if no timestamp available
          updatedTask.endTime = new Date();
        }

        ClientLoggingService.log(
          'INFO',
          `[TaskCounterService] 🎯 Task ${taskId} transitioning from '${previousStatus}' to '${taskStatus.status}' - setting endTime and unsubscribing`
        );

        // Unsubscribe from updates for completed tasks
        const subscription = this.taskSubscriptions.get(taskId);
        if (subscription) {
          subscription.unsubscribe();
          this.taskSubscriptions.delete(taskId);
          ClientLoggingService.log('INFO', `[TaskCounterService] ✅ Unsubscribed from task ${taskId}`);
        } else {
          ClientLoggingService.log(
            'WARN',
            `[TaskCounterService] ⚠️ No subscription found for completed task ${taskId}`
          );
        }
      }

      const updatedTasks = [...currentTasks];
      updatedTasks[taskIndex] = updatedTask;
      this.activeTasksSubject.next(updatedTasks);

      ClientLoggingService.log(
        'INFO',
        `[TaskCounterService] ✅ Updated task ${taskId}: ${previousStatus} → ${taskStatus.status} (${taskStatus.progress}%)`
      );

      // Debug: Log current task counts after update
      const taskCounts = this.getCurrentTaskCount();
      ClientLoggingService.log('INFO', `[TaskCounterService] 📊 Current task counts:`, taskCounts);
    } else {
      ClientLoggingService.log(
        'WARN',
        `[TaskCounterService] ⚠️ Task ${taskId} not found in active tasks list for update`
      );
    }
  }

  /**
   * Remove old completed/failed tasks (cleanup)
   */
  cleanupOldTasks(maxAge: number = 24 * 60 * 60 * 1000): void {
    // 24 hours default
    const now = new Date();
    const currentTasks = this.activeTasksSubject.value;

    const filteredTasks = currentTasks.filter(task => {
      if (task.status === 'pending' || task.status === 'processing') {
        return true; // Keep active tasks
      }

      const taskAge = now.getTime() - (task.endTime || task.startTime).getTime();
      return taskAge < maxAge; // Keep recent completed tasks
    });

    if (filteredTasks.length !== currentTasks.length) {
      this.activeTasksSubject.next(filteredTasks);
      ClientLoggingService.log('INFO', '[TaskCounterService] Cleaned up old tasks');
    }
  }

  /**
   * Get tasks for a specific project
   */
  getTasksForProject(projectId: string): ActiveTask[] {
    return this.activeTasksSubject.value.filter(task => task.projectId === projectId);
  }

  /**
   * Load existing tasks for a project from Firestore. This populates the task counter with
   * the first (most recent) page of historical tasks. Always starts pagination over from
   * the first page — call this again (e.g. on re-navigation) to refresh from the top.
   *
   * See Issue #1598: the Tasks page used to hard-cap at this first page with no way to see
   * older tasks. Use `loadMoreTasksForProject()` / `hasMoreTasksForProject()` to page further.
   */
  async loadExistingTasksForProject(projectId: string): Promise<void> {
    this.projectPagination.delete(projectId);
    await this.loadTasksPage(projectId, null);
  }

  /**
   * Loads the next page of older tasks for a project, appending them to `activeTasks$`.
   * No-op (resolves to `false`) if `loadExistingTasksForProject()` hasn't been called yet,
   * or if there is no further page. Returns whether a further page remains after this load.
   */
  async loadMoreTasksForProject(projectId: string): Promise<boolean> {
    const state = this.projectPagination.get(projectId);
    if (!state?.hasMore) {
      return false;
    }
    await this.loadTasksPage(projectId, state.lastVisibleDoc);
    return this.hasMoreTasksForProject(projectId);
  }

  /** Whether a further page of older tasks exists for this project (Issue #1598). */
  hasMoreTasksForProject(projectId: string): boolean {
    return this.projectPagination.get(projectId)?.hasMore ?? false;
  }

  /**
   * Fetches one page of tasks for a project and merges any not-already-tracked tasks into
   * `activeTasks$`, updating the pagination cursor for subsequent `loadMoreTasksForProject()`
   * calls. Shared by the initial load and "load more" so both go through the exact same
   * merge/subscribe logic.
   */
  private async loadTasksPage(
    projectId: string,
    startAfterDoc: QueryDocumentSnapshot<DocumentData> | null
  ): Promise<void> {
    ClientLoggingService.log(
      'INFO',
      '[TaskCounterService] Loading tasks page for project:',
      projectId,
      startAfterDoc ? '(next page)' : '(first page)'
    );

    try {
      const page = await this.firestoreTaskTrackingService.getTasksForProject(projectId, 50, startAfterDoc);
      this.projectPagination.set(projectId, { lastVisibleDoc: page.lastVisibleDoc, hasMore: page.hasMore });

      ClientLoggingService.log(
        'INFO',
        '[TaskCounterService] Found',
        page.tasks.length,
        'tasks for project:',
        projectId,
        '(hasMore:',
        page.hasMore,
        ')'
      );

      const currentTasks = this.activeTasksSubject.value;
      const newTasks = this.buildActiveTasksFromStatuses(page.tasks, projectId, currentTasks);

      if (newTasks.length > 0) {
        const updatedTasks = [...currentTasks, ...newTasks];
        this.activeTasksSubject.next(updatedTasks);
        ClientLoggingService.log(
          'INFO',
          '[TaskCounterService] Added',
          newTasks.length,
          'existing tasks. Total tasks:',
          updatedTasks.length
        );
      } else {
        ClientLoggingService.log('INFO', '[TaskCounterService] No new existing tasks to add (all already tracked)');
      }
    } catch (error) {
      ClientLoggingService.log(
        'ERROR',
        '[TaskCounterService] Error loading existing tasks for project:',
        projectId,
        error
      );
      // Rethrow so callers (e.g. TasksPageComponent) can surface the failure to the user
      // instead of silently showing an incomplete task list.
      throw error;
    }
  }

  /**
   * Converts a page of Firestore `TaskStatus` results into `ActiveTask`s, skipping any
   * already present in `currentTasks`, and subscribes to real-time updates for non-terminal
   * ones. Returns only the newly-added tasks (callers append them to `activeTasksSubject`).
   */
  private buildActiveTasksFromStatuses(
    existingTasks: TaskStatus[],
    projectId: string,
    currentTasks: ActiveTask[]
  ): ActiveTask[] {
    const newTasks: ActiveTask[] = [];

    for (const taskStatus of existingTasks) {
      // Check if we already have this task to avoid duplicates
      const existingTask = currentTasks.find(t => t.taskId === taskStatus.taskId);
      if (!existingTask) {
        // Extract filename and page number from metadata if available
        let filename = 'Unknown File';
        if (taskStatus.metadata) {
          ClientLoggingService.log(
            'INFO',
            `[TaskCounterService] Task ${taskStatus.taskId} metadata:`,
            taskStatus.metadata
          );

          const baseFilename = taskStatus.metadata['filename'];
          const pageNumber = taskStatus.metadata['pageNumber'] || taskStatus.metadata['page_number'];

          ClientLoggingService.log(
            'INFO',
            `[TaskCounterService] Extracted - filename: ${baseFilename}, pageNumber: ${pageNumber}`
          );

          if (baseFilename) {
            // Store just the base filename without page number suffix
            filename = baseFilename;
          }
        }

        // Parse timestamps from Firestore data
        let startTime = new Date(); // Default fallback
        let endTime: Date | undefined = undefined;

        // Use real timestamps from Firestore if available
        if (taskStatus.createdAt) {
          try {
            const parsedDate = new Date(taskStatus.createdAt);
            if (!isNaN(parsedDate.getTime())) {
              startTime = parsedDate;
            } else {
              ClientLoggingService.log(
                'WARN',
                `[TaskCounterService] Invalid createdAt timestamp: ${taskStatus.createdAt}`
              );
            }
          } catch (e) {
            ClientLoggingService.log(
              'WARN',
              `[TaskCounterService] Failed to parse createdAt timestamp: ${taskStatus.createdAt}`,
              e
            );
          }
        } else if (taskStatus.startedAt) {
          try {
            const parsedDate = new Date(taskStatus.startedAt);
            if (!isNaN(parsedDate.getTime())) {
              startTime = parsedDate;
            } else {
              ClientLoggingService.log(
                'WARN',
                `[TaskCounterService] Invalid startedAt timestamp: ${taskStatus.startedAt}`
              );
            }
          } catch (e) {
            ClientLoggingService.log(
              'WARN',
              `[TaskCounterService] Failed to parse startedAt timestamp: ${taskStatus.startedAt}`,
              e
            );
          }
        }

        // Set end time based on task status and available timestamps
        if (taskStatus.status === 'complete' && taskStatus.completedAt) {
          try {
            const parsedDate = new Date(taskStatus.completedAt);
            if (!isNaN(parsedDate.getTime())) {
              endTime = parsedDate;
            } else {
              ClientLoggingService.log(
                'WARN',
                `[TaskCounterService] Invalid completedAt timestamp: ${taskStatus.completedAt}`
              );
              endTime = new Date(); // Fallback to current time
            }
          } catch (e) {
            ClientLoggingService.log(
              'WARN',
              `[TaskCounterService] Failed to parse completedAt timestamp: ${taskStatus.completedAt}`,
              e
            );
            endTime = new Date(); // Fallback to current time
          }
        } else if (taskStatus.status === 'failed' && taskStatus.failedAt) {
          try {
            const parsedDate = new Date(taskStatus.failedAt);
            if (!isNaN(parsedDate.getTime())) {
              endTime = parsedDate;
            } else {
              ClientLoggingService.log(
                'WARN',
                `[TaskCounterService] Invalid failedAt timestamp: ${taskStatus.failedAt}`
              );
              endTime = new Date(); // Fallback to current time
            }
          } catch (e) {
            ClientLoggingService.log(
              'WARN',
              `[TaskCounterService] Failed to parse failedAt timestamp: ${taskStatus.failedAt}`,
              e
            );
            endTime = new Date(); // Fallback to current time
          }
        } else if (taskStatus.status === 'cancelled' && taskStatus.cancelledAt) {
          try {
            const parsedDate = new Date(taskStatus.cancelledAt);
            if (!isNaN(parsedDate.getTime())) {
              endTime = parsedDate;
            } else {
              ClientLoggingService.log(
                'WARN',
                `[TaskCounterService] Invalid cancelledAt timestamp: ${taskStatus.cancelledAt}`
              );
              endTime = new Date(); // Fallback to current time
            }
          } catch (e) {
            ClientLoggingService.log(
              'WARN',
              `[TaskCounterService] Failed to parse cancelledAt timestamp: ${taskStatus.cancelledAt}`,
              e
            );
            endTime = new Date(); // Fallback to current time
          }
        }

        const activeTask: ActiveTask = {
          taskId: taskStatus.taskId,
          projectId: projectId,
          filename: filename,
          taskType: taskStatus.taskType,
          status: taskStatus.status,
          progress: taskStatus.progress,
          message: taskStatus.message,
          startTime: startTime,
          endTime: endTime,
          progressSteps: taskStatus.progressSteps,
          metadata: { ...taskStatus.metadata, ...taskStatus.result },
          costAnalysis: taskStatus.costAnalysis,
        };

        newTasks.push(activeTask);

        // Subscribe to real-time updates for this task if it's not completed
        // Include failed tasks so they remain visible in the UI
        if (taskStatus.status === 'pending' || taskStatus.status === 'processing' || taskStatus.status === 'failed') {
          const subscription = this.firestoreTaskTrackingService.getTaskUpdates(taskStatus.taskId).subscribe({
            next: (updatedTaskStatus: TaskStatus) => {
              // Reduced logging - only log significant updates
              if (updatedTaskStatus.status === 'complete' || updatedTaskStatus.status === 'failed') {
                ClientLoggingService.log(
                  'INFO',
                  '[TaskCounterService] Task completed:',
                  taskStatus.taskId,
                  updatedTaskStatus.status
                );
              }
              this.updateTaskStatus(taskStatus.taskId, updatedTaskStatus);
            },
            error: error => {
              ClientLoggingService.log(
                'ERROR',
                '[TaskCounterService] Error tracking existing task:',
                taskStatus.taskId,
                error
              );
              this.updateTaskStatus(taskStatus.taskId, {
                taskId: taskStatus.taskId,
                status: 'failed',
                progress: 0,
                message: 'Failed to track task',
              });
            },
          });

          this.taskSubscriptions.set(taskStatus.taskId, subscription);
        }
      }
    }

    return newTasks;
  }

  /**
   * Clear all tasks (for testing/reset)
   */
  clearAllTasks(): void {
    // Unsubscribe from all task updates
    this.taskSubscriptions.forEach(subscription => subscription.unsubscribe());
    this.taskSubscriptions.clear();
    this.projectPagination.clear();

    this.activeTasksSubject.next([]);
    ClientLoggingService.log('INFO', '[TaskCounterService] Cleared all tasks');
  }

  /**
   * Get current task count for debugging
   */
  getCurrentTaskCount(): { total: number; active: number; completed: number; failed: number } {
    const tasks = this.activeTasksSubject.value;
    return {
      total: tasks.length,
      active: (tasks || []).filter(t => t.status === 'pending' || t.status === 'processing').length,
      completed: (tasks || []).filter(t => t.status === 'complete').length,
      failed: (tasks || []).filter(t => t.status === 'failed').length,
    };
  }

  /**
   * Get all current tasks (for debugging)
   */
  getCurrentTasks(): ActiveTask[] {
    return this.activeTasksSubject.value;
  }

  /**
   * Cancels a task by updating its status in Firestore.
   * The task will be automatically updated in the UI via Firestore real-time updates.
   */
  async cancelTask(taskId: string, reason?: string): Promise<boolean> {
    ClientLoggingService.log('INFO', `[TaskCounterService] Cancelling task: ${taskId}`);
    return await this.firestoreTaskTrackingService.cancelTask(taskId, reason);
  }

  /**
   * Get an observable for a specific task that's managed by this service.
   * This avoids duplicate Firestore subscriptions by using the centralized task data.
   */
  getTaskObservable(taskId: string): Observable<ActiveTask | null> {
    return this.activeTasks$.pipe(map(tasks => tasks.find(task => task.taskId === taskId) || null));
  }

  /**
   * Check if a task is currently being tracked by this service
   */
  isTaskTracked(taskId: string): boolean {
    return this.activeTasksSubject.value.some(task => task.taskId === taskId);
  }
}
